/* eslint-disable */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './TerraformSimulator.css';

// Simple resource templates
const resourceTemplates = {
  aws_instance: {
    requiredFields: ['ami', 'instance_type'],
    optionalFields: ['tags', 'subnet_id', 'vpc_security_group_ids'],
    fieldExplanations: {
      ami: 'Amazon Machine Image ID that specifies the operating system and configuration.',
      instance_type: 'The type of EC2 instance, which determines CPU, memory, storage and networking capacity.',
      tags: 'Key-value metadata tags for the instance.',
      subnet_id: 'The VPC Subnet ID to launch the instance in.',
      vpc_security_group_ids: 'A list of security group IDs to associate with the instance.'
    },
    example: 'resource "aws_instance" "web" {\n  ami           = "ami-0c55b159cbfafe1f0"\n  instance_type = "t2.micro"\n\n  tags = {\n    Name = "web-server"\n  }\n}'
  },
  aws_s3_bucket: {
    requiredFields: ['bucket'],
    optionalFields: ['acl', 'tags', 'versioning'],
    fieldExplanations: {
      bucket: 'The name of the bucket. Must be globally unique across all AWS accounts.',
      acl: 'The canned ACL to apply to the bucket.',
      tags: 'Key-value metadata tags for the bucket.',
      versioning: 'Configuration block for versioning parameters.'
    },
    example: 'resource "aws_s3_bucket" "data" {\n  bucket = "my-data-bucket"\n  acl    = "private"\n\n  versioning {\n    enabled = true\n  }\n}'
  }
};

// Practice task data
const practiceTasksData = [
  {
    id: 1,
    title: "Define AWS EC2 Instance",
    description: "Complete the resource block for an AWS EC2 instance by filling in the blanks.",
    code: `# Define an AWS EC2 instance
resource "aws_instance" "web" {
  ami           = "ami-0c55b159cbfafe1f0"
  instance_type = ________ # Fill in: use a t2.micro instance type
  
  tags = {
    Name = "web-server"
    Environment = ________  # Fill in: specify the environment as "development"
  }
}`,
    solutions: [
      {
        blank: "instance_type = ________",
        answer: "t2.micro"
      },
      {
        blank: "Environment = ________",
        answer: "development"
      }
    ],
    hint: "Use 't2.micro' for a small instance type and 'development' for the environment tag."
  },
  {
    id: 2,
    title: "S3 Bucket with Versioning",
    description: "Create an S3 bucket with versioning enabled by filling in the blanks.",
    code: `# Create an S3 bucket with versioning
resource "________" "logs" {
  bucket = "my-app-logs"
  acl    = "private"

  versioning {
    ________ = true
  }
}`,
    solutions: [
      {
        blank: "resource \"________\"",
        answer: "aws_s3_bucket"
      },
      {
        blank: "________ = true",
        answer: "enabled"
      }
    ],
    hint: "Use 'aws_s3_bucket' as the resource type and 'enabled' for versioning."
  },
  {
    id: 3,
    title: "Variable Definition",
    description: "Define a variable to store the region with a default value.",
    code: `# Define the AWS region variable
________ "aws_region" {
  description = "AWS region to deploy resources"
  type        = ________
  default     = "us-west-2"
}`,
    solutions: [
      {
        blank: "________ \"aws_region\"",
        answer: "variable"
      },
      {
        blank: "type        = ________",
        answer: "string"
      }
    ],
    hint: "Use 'variable' to define a variable and 'string' as the type for text values."
  },
  {
    id: 4,
    title: "Output Definition",
    description: "Define an output to expose the S3 bucket ARN.",
    code: `# Create an S3 bucket
resource "aws_s3_bucket" "data" {
  bucket = "my-app-data"
}

# Define an output for the bucket ARN
________ "bucket_arn" {
  value       = ________
  description = "The ARN of the S3 bucket"
}`,
    solutions: [
      {
        blank: "________ \"bucket_arn\"",
        answer: "output"
      },
      {
        blank: "value       = ________",
        answer: "aws_s3_bucket.data.arn"
      }
    ],
    hint: "Use 'output' to define an output and reference the bucket ARN with 'aws_s3_bucket.data.arn'."
  },
  {
    id: 5,
    title: "Data Source Usage",
    description: "Complete the data source block to fetch information about an existing VPC.",
    code: `# Fetch information about an existing VPC
________ "existing_vpc" {
  id = "vpc-12345678"
}

# Output the CIDR block of the VPC
output "vpc_cidr" {
  value = ________
}`,
    solutions: [
      {
        blank: "________ \"existing_vpc\"",
        answer: "data \"aws_vpc\""
      },
      {
        blank: "value = ________",
        answer: "data.aws_vpc.existing_vpc.cidr_block"
      }
    ],
    hint: "Use 'data \"aws_vpc\"' to query information about an existing VPC and reference its CIDR block with 'data.aws_vpc.existing_vpc.cidr_block'."
  },
  {
    id: 6,
    title: "Provider Configuration",
    description: "Configure the AWS provider with a specific region and profile.",
    code: `# Configure the AWS provider
________ "aws" {
  region  = "us-west-2"
  profile = ________
}`,
    solutions: [
      {
        blank: "________ \"aws\"",
        answer: "provider"
      },
      {
        blank: "profile = ________",
        answer: "\"default\""
      }
    ],
    hint: "Use 'provider' to configure a provider and \"default\" (with quotes) for the profile name."
  },
  {
    id: 7,
    title: "Resource Dependency",
    description: "Create a security group that depends on a VPC resource.",
    code: `# Create a VPC
resource "aws_vpc" "main" {
  cidr_block = "10.0.0.0/16"
}

# Create a security group in the VPC
resource "aws_security_group" "allow_http" {
  name        = "allow_http"
  description = "Allow HTTP inbound traffic"
  ________ = ________  # Fill in: reference the VPC ID

  ingress {
    from_port   = 80
    to_port     = 80
    protocol    = "tcp"
    cidr_blocks = ["0.0.0.0/0"]
  }
}`,
    solutions: [
      {
        blank: "________ = ________",
        answer: "vpc_id = aws_vpc.main.id"
      }
    ],
    hint: "Use 'vpc_id = aws_vpc.main.id' to reference the ID of the VPC resource."
  },
  {
    id: 8,
    title: "Count Parameter",
    description: "Use the count parameter to create multiple similar resources.",
    code: `# Create multiple EC2 instances
resource "aws_instance" "server" {
  ________ = ________  # Fill in: create 3 instances
  
  ami           = "ami-0c55b159cbfafe1f0"
  instance_type = "t2.micro"
  
  tags = {
    Name = "server-\${________}"  # Fill in: use the index in the name
  }
}`,
    solutions: [
      {
        blank: "________ = ________",
        answer: "count = 3"
      },
      {
        blank: "Name = \"server-\${________}\"",
        answer: "count.index"
      }
    ],
    hint: "Use 'count = 3' to create 3 instances and 'count.index' to access the current instance index (0, 1, 2)."
  },
  {
    id: 9,
    title: "For Each Loop",
    description: "Use for_each to create multiple resources based on a map.",
    code: `# Define instance configurations
locals {
  instances = {
    web = {
      instance_type = "t2.micro"
      environment   = "dev"
    },
    db = {
      instance_type = "t2.medium"
      environment   = "prod"
    }
  }
}

# Create instances based on configurations
resource "aws_instance" "servers" {
  ________ = ________  # Fill in: iterate over the instances map
  
  ami           = "ami-0c55b159cbfafe1f0"
  instance_type = ________  # Fill in: reference the current instance type
  
  tags = {
    Name        = ________  # Fill in: use the map key as name
    Environment = each.value.environment
  }
}`,
    solutions: [
      {
        blank: "________ = ________",
        answer: "for_each = local.instances"
      },
      {
        blank: "instance_type = ________",
        answer: "each.value.instance_type"
      },
      {
        blank: "Name        = ________",
        answer: "each.key"
      }
    ],
    hint: "Use 'for_each = local.instances' to iterate over the map, 'each.value.instance_type' to access the instance type, and 'each.key' for the name."
  },
  {
    id: 10,
    title: "Conditional Resource Creation",
    description: "Use a conditional expression to decide whether to create a resource.",
    code: `# Variable to control resource creation
variable "create_database" {
  description = "Whether to create the database"
  type        = bool
  default     = true
}

# Create a database conditionally
resource "aws_db_instance" "database" {
  ________ = ________  # Fill in: create only if variable is true
  
  allocated_storage    = 10
  engine               = "mysql"
  engine_version       = "5.7"
  instance_class       = "db.t3.micro"
  name                 = "mydb"
  username             = "admin"
  password             = "password123"
  parameter_group_name = "default.mysql5.7"
  skip_final_snapshot  = true
}`,
    solutions: [
      {
        blank: "________ = ________",
        answer: "count = var.create_database ? 1 : 0"
      }
    ],
    hint: "Use 'count = var.create_database ? 1 : 0' to create the resource only if the variable is true."
  },
];

function TerraformSimulator() {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [showSolution, setShowSolution] = useState(false);
  const [feedback, setFeedback] = useState("");

  const currentTask = practiceTasksData[currentTaskIndex];

  const handleAnswerChange = (blank, value) => {
    setUserAnswers({
      ...userAnswers,
      [blank]: value
    });
  };

  const checkAnswers = () => {
    const task = practiceTasksData[currentTaskIndex];
    const allCorrect = task.solutions.every(solution => 
      userAnswers[solution.blank]?.trim().toLowerCase() === solution.answer.toLowerCase()
    );

    if (allCorrect) {
      setFeedback("Correct! All answers are right.");
    } else {
      setFeedback("Some answers are incorrect. Try again or check the solution.");
    }
  };

  const resetTask = () => {
    setUserAnswers({});
    setShowSolution(false);
    setFeedback("");
  };

  const nextTask = () => {
    resetTask();
    setCurrentTaskIndex((currentTaskIndex + 1) % practiceTasksData.length);
  };

  const prevTask = () => {
    resetTask();
    setCurrentTaskIndex((currentTaskIndex - 1 + practiceTasksData.length) % practiceTasksData.length);
  };

  const renderCodeWithInputs = () => {
    let codeWithBlanks = currentTask.code;
    
    // Create a temporary div to hold the code
    const codeDiv = document.createElement('div');
    codeDiv.textContent = codeWithBlanks;
    
    // Replace blanks with input fields
    currentTask.solutions.forEach(solution => {
      const blank = solution.blank;
      
      // Create a simple display version to show instead of inputs
      codeWithBlanks = codeWithBlanks.replace(blank, `[BLANK: ${blank}]`);
    });

    return (
      <div>
        <pre 
          style={{ 
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            padding: '15px',
            backgroundColor: '#282c34',
            color: '#abb2bf',
            borderRadius: '5px',
            lineHeight: '1.6'
          }}
        >
          {codeWithBlanks}
        </pre>
        
        <div style={{ marginTop: '20px' }}>
          <h3>Your Answers:</h3>
          {currentTask.solutions.map((solution, index) => (
            <div key={index} style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                For: <code>{solution.blank}</code>
              </label>
              <input
                type="text"
                value={userAnswers[solution.blank] || ''}
                onChange={(e) => handleAnswerChange(solution.blank, e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  borderRadius: '4px',
                  border: '1px solid #ccc'
                }}
                placeholder="Type your answer here"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ color: '#333', marginBottom: '20px' }}>Terraform Syntax Simulator</h1>
      <p style={{ marginBottom: '30px' }}>
        Practice your Terraform syntax by completing code blocks. Fill in the blanks to create valid Terraform configurations.
      </p>

      <div style={{ 
        background: '#f5f5f5', 
        padding: '20px', 
        borderRadius: '8px',
        marginBottom: '30px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '15px'
        }}>
          <h2 style={{ margin: 0 }}>Task {currentTask.id}: {currentTask.title}</h2>
          <div>
            <button 
              onClick={prevTask}
              style={{ 
                marginRight: '10px',
                padding: '8px 15px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Previous Task
            </button>
            <button 
              onClick={nextTask}
              style={{ 
                padding: '8px 15px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Next Task
            </button>
          </div>
        </div>
        
        <p style={{ marginBottom: '15px' }}>{currentTask.description}</p>
        
        <div style={{ marginBottom: '20px' }}>
          {renderCodeWithInputs()}
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <button 
            onClick={checkAnswers}
            style={{ 
              marginRight: '10px',
              padding: '8px 15px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Check Answers
          </button>
          <button 
            onClick={() => setShowSolution(!showSolution)}
            style={{ 
              marginRight: '10px',
              padding: '8px 15px',
              backgroundColor: showSolution ? '#f44336' : '#FF9800',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {showSolution ? "Hide Solution" : "Show Solution"}
          </button>
          <button 
            onClick={resetTask}
            style={{ 
              padding: '8px 15px',
              backgroundColor: '#607D8B',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Reset
          </button>
        </div>
        
        {feedback && (
          <div style={{ 
            padding: '15px', 
            backgroundColor: feedback.includes('Correct') ? '#e8f5e9' : '#ffebee',
            borderRadius: '4px',
            marginBottom: '20px',
            color: feedback.includes('Correct') ? '#2e7d32' : '#c62828'
          }}>
            {feedback}
          </div>
        )}
        
        {showSolution && (
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#e3f2fd', 
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            <h3 style={{ marginTop: 0 }}>Solution:</h3>
            <ul>
              {currentTask.solutions.map((solution, index) => (
                <li key={index} style={{ marginBottom: '5px' }}>
                  <code style={{ 
                    backgroundColor: '#f1f8e9', 
                    padding: '3px 5px', 
                    borderRadius: '3px' 
                  }}>
                    {solution.blank}
                  </code>: 
                  <code style={{ 
                    backgroundColor: '#e8f5e9', 
                    padding: '3px 5px', 
                    borderRadius: '3px',
                    marginLeft: '5px' 
                  }}>
                    {solution.answer}
                  </code>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#fff3e0', 
          borderRadius: '4px' 
        }}>
          <h3 style={{ marginTop: 0 }}>Hint:</h3>
          <p style={{ margin: 0 }}>{currentTask.hint}</p>
        </div>
      </div>
      
      <Link to="/" style={{ 
        padding: '10px 20px', 
        backgroundColor: '#4CAF50', 
        color: 'white', 
        textDecoration: 'none', 
        borderRadius: '4px',
        display: 'inline-block'
      }}>
        Back to Home
      </Link>
    </div>
  );
}

export default TerraformSimulator;
